import React from 'react'
import { useHistory } from 'react-router'
import { Messages1 } from 'iconsax-react'
import { useTheme } from '@mui/material'

import { FavoriteCardProps } from '..'
import { Routes } from '@SonarCX/Routes'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { selectDMs } from '@SonarCX/slices/dms'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'

import FavoriteButton from '@SonarCX/components/atoms/FavoriteButton'
import Tooltip from '@SonarCX/components/atoms/Tooltip'

import useStyles from '../styles'
import DraggableContainer from '../DraggableContainer'

const FavoritePeerChat: React.FC<FavoriteCardProps> = ({
  favorite,
  searchValue,
  draggable,
  handleMoveFavorite
}) => {
  const [usersSet, setUsersSet] = React.useState(false)
  const theme = useTheme()
  const classes = useStyles({})
  const history = useHistory()
  const { dms } = useAppSelector(selectDMs)
  const { user: currentUser } = useEnvironment()
  const {
    api: { fetchTimeline }
  } = useSocket()
  const {
    allUsers,
    loaded: usersLoaded,
    loaded: userLoading,
    loadUser
  } = useUserAPI()
  const fullSession = React.useMemo(
    () => dms[favorite.item_id] ?? null,
    [dms, favorite]
  )

  // Comma separated list of user names
  const convoName = React.useMemo(() => {
    if (!fullSession) return ''

    if (fullSession.name) {
      return fullSession.name
    }

    const names = []
    fullSession.userIds
      .filter(id => id !== currentUser?.id)
      .forEach(id => {
        const user = allUsers[id]
        if (user) {
          names.push(user.name)
        } else {
          loadUser(id)
        }
      })
    return names.join(', ')
  }, [fullSession, currentUser?.id, allUsers, loadUser])

  const navigate = () => {
    sessionStorage.removeItem('last_convo')
    const path = `${Routes.PEER_CHAT}?session=${favorite.item_id}${
      fullSession.isGroup ? '' : '&isUser=true'
    }`
    history.replace(path)
  }
  // Gets the full timeline
  React.useEffect(() => {
    if (fullSession || !usersLoaded || userLoading) {
      return
    }
    // condition for searching user or session for dms
    const userExists = allUsers[favorite.item_id]
    const isGroup = !!userExists
    fetchTimeline(favorite.item_id, true, isGroup)
  }, [allUsers, favorite, fetchTimeline, fullSession, userLoading, usersLoaded])

  React.useEffect(() => {
    if (!fullSession || usersSet) return

    fullSession.users.forEach(id => {
      const user = allUsers[id]
      if (!user) {
        loadUser(id)
      }
    })
    setUsersSet(true)
  }, [allUsers, fullSession, loadUser, usersSet])

  if (!fullSession) return null

  if (
    searchValue.length > 0 &&
    !convoName.toLowerCase().includes(searchValue.toLowerCase())
  )
    return null

  return (
    <DraggableContainer
      favorite={favorite}
      draggable={draggable}
      handleMoveFavorite={handleMoveFavorite}
    >
      <div onClick={navigate}>
        <span className={classes.info}>
          <FavoriteButton
            isFavorite
            type={favorite.type}
            itemId={favorite.item_id}
          />
          <Tooltip title='Team Chat'>
            <Messages1
              size={theme.size.base}
              variant='Bulk'
              color={theme.palette.primary.main}
            />
          </Tooltip>

          <span className={classes.title}> {convoName}</span>
        </span>
      </div>
    </DraggableContainer>
  )
}

export default FavoritePeerChat
